import React, { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  CardDescription,
} from "@/components/ui";
import { ThemeToggle } from "./ThemeToggle";
import { Checkbox } from "../components/ui/checkbox";
import { SimpleThemeToggle } from "./SimpleThemeToggle";
import {
  RefreshCw,
  Palette,
  MoonStar,
  SunMoon,
  Check,
  Settings,
  User,
  Plus,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AnalyticsNumberPanel } from "./AnalyticsNumberPanel";
import { AnalyticsDonutChart } from "./AnalyticsDonutChart";
import { AnalyticsBarChart } from "./AnalyticsBarChart";
import { AnalyticsCalendar, CalendarEventType } from "./AnalyticsCalendar";
import { AnalyticsTaskList, TaskStatus } from "./AnalyticsTaskList";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";

export const ThemeShowcase = () => {
  const { theme, setTheme } = useTheme();
  const [forceUpdate, setForceUpdate] = useState(0);
  const [customThemeEnabled, setCustomThemeEnabled] = useState(false);

  // State for form controls
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date()
  );
  const [progressValue, setProgressValue] = useState(65);

  // Force re-render to update colors
  const handleForceUpdate = () => {
    setForceUpdate((prev) => prev + 1);
  };

  // Add a key prop to force re-render when theme changes
  useEffect(() => {
    handleForceUpdate();
    // Check if custom theme is enabled
    const storedCustomTheme = localStorage.getItem("customThemeEnabled");
    setCustomThemeEnabled(storedCustomTheme === "true");
  }, [theme]);

  // Sample data for donut charts
  const portfolioAllocationData = [
    { label: "Stocks", value: 45, color: "var(--primary)" },
    { label: "Bonds", value: 30, color: "var(--secondary)" },
    { label: "Cash", value: 15, color: "var(--accent)" },
    { label: "Alternatives", value: 10, color: "var(--destructive)" },
  ];

  const marketShareData = [
    { label: "WealthBee", value: 35, color: "var(--primary)" },
    { label: "Competitor A", value: 25, color: "var(--secondary)" },
    { label: "Competitor B", value: 20, color: "var(--accent)" },
    { label: "Others", value: 20, color: "var(--muted-foreground)" },
  ];

  const customerSegmentData = [
    { label: "Premium", value: 15, color: "var(--primary-150)" },
    { label: "Plus", value: 30, color: "var(--primary)" },
    { label: "Standard", value: 55, color: "var(--primary-100)" },
  ];

  // Sample data for bar charts
  const monthlySalesData = [
    { label: "Jan", value: 45000, color: "var(--primary)" },
    { label: "Feb", value: 52000, color: "var(--primary)" },
    { label: "Mar", value: 48000, color: "var(--primary)" },
    { label: "Apr", value: 61000, color: "var(--primary)" },
    { label: "May", value: 55000, color: "var(--primary)" },
    { label: "Jun", value: 67000, color: "var(--primary)" },
    { label: "Jul", value: 72000, color: "var(--primary)" },
    { label: "Aug", value: 60000, color: "var(--primary)" },
  ];

  const categoryPerformanceData = [
    { label: "Equities", value: 12.5, color: "var(--primary)" },
    { label: "Fixed Income", value: 4.2, color: "var(--secondary)" },
    { label: "Real Estate", value: 7.8, color: "var(--accent)" },
    { label: "Commodities", value: -3.2, color: "var(--destructive)" },
    { label: "Crypto", value: 22.1, color: "var(--tertiary-100)" },
  ];

  const topInvestmentsData = [
    { label: "AAPL", value: 2450000, color: "var(--primary)" },
    { label: "MSFT", value: 1850000, color: "var(--primary-110)" },
    { label: "AMZN", value: 1650000, color: "var(--primary-150)" },
    { label: "GOOGL", value: 1350000, color: "var(--primary-170)" },
    { label: "TSLA", value: 950000, color: "var(--accent)" },
  ];

  const customerRatingsData = [
    { label: "Product", value: 4.5, color: "var(--primary)" },
    { label: "Support", value: 4.3, color: "var(--primary)" },
    { label: "UX", value: 4.7, color: "var(--primary)" },
    { label: "Performance", value: 4.1, color: "var(--primary)" },
    { label: "Value", value: 4.2, color: "var(--primary)" },
  ];

  const ageDistributionData = [
    { label: "18-24", value: 15, color: "var(--primary-100)" },
    { label: "25-34", value: 32, color: "var(--primary-110)" },
    { label: "35-44", value: 27, color: "var(--primary)" },
    { label: "45-54", value: 18, color: "var(--primary-150)" },
    { label: "55+", value: 8, color: "var(--primary-170)" },
  ];

  // Sample data for calendar events
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  // Financial calendar events
  const financialCalendarEvents = [
    {
      date: new Date(currentYear, currentMonth, 5),
      type: "info" as CalendarEventType,
      label: "Quarterly Report",
    },
    {
      date: new Date(currentYear, currentMonth, 10),
      type: "warning" as CalendarEventType,
      label: "Tax Deadline",
    },
    {
      date: new Date(currentYear, currentMonth, 15),
      type: "success" as CalendarEventType,
      label: "Dividends Paid",
    },
    {
      date: new Date(currentYear, currentMonth, 20),
      type: "error" as CalendarEventType,
      label: "Market Closure",
    },
    {
      date: new Date(currentYear, currentMonth, 25),
      type: "closed" as CalendarEventType,
      label: "Trading Closed",
    },
    {
      date: new Date(currentYear, currentMonth, 28),
      type: "open" as CalendarEventType,
      label: "New Fund Available",
    },
  ];

  // Trading period calendar
  const tradingCalendarEvents = [
    {
      date: new Date(currentYear, currentMonth, 1),
      type: "open" as CalendarEventType,
      label: "Market Open",
    },
    {
      date: new Date(currentYear, currentMonth, 8),
      type: "closed" as CalendarEventType,
      label: "Holiday",
    },
    {
      date: new Date(currentYear, currentMonth, 12),
      type: "info" as CalendarEventType,
      label: "Fed Meeting",
    },
    {
      date: new Date(currentYear, currentMonth, 15),
      type: "warning" as CalendarEventType,
      label: "Earnings Season",
    },
    {
      date: new Date(currentYear, currentMonth, 24),
      type: "closed" as CalendarEventType,
      label: "Holiday",
    },
    {
      date: new Date(currentYear, currentMonth, 30),
      type: "success" as CalendarEventType,
      label: "Month End Reports",
    },
  ];

  // Sample data for task lists
  const fundNAVTasks = [
    {
      id: "f1",
      title: "Global Equity Fund",
      subtitle: "Monthly NAV Calculation",
      status: "completed" as TaskStatus,
      metadata: {
        deadline: new Date(2023, currentDate.getMonth(), 5, 17, 0),
        manager: "Sarah Johnson",
        aum: "$245.8M",
        change: "+1.2%",
      },
      priority: "high" as const,
    },
    {
      id: "f2",
      title: "Fixed Income Opportunities",
      subtitle: "Quarterly NAV Review",
      status: "in-progress" as TaskStatus,
      metadata: {
        deadline: new Date(
          2023,
          currentDate.getMonth(),
          currentDate.getDate() + 2,
          17,
          0
        ),
        manager: "David Chen",
        aum: "$180.5M",
        change: "+0.3%",
      },
      priority: "medium" as const,
    },
    {
      id: "f3",
      title: "Emerging Markets Fund",
      subtitle: "Monthly NAV Calculation",
      status: "pending" as TaskStatus,
      metadata: {
        deadline: new Date(
          2023,
          currentDate.getMonth(),
          currentDate.getDate() + 3,
          17,
          0
        ),
        manager: "Robert Hines",
        aum: "$120.7M",
        change: "-0.5%",
      },
      priority: "medium" as const,
    },
    {
      id: "f4",
      title: "Alternative Strategies Fund",
      subtitle: "Weekly NAV Update",
      status: "overdue" as TaskStatus,
      metadata: {
        deadline: new Date(
          2023,
          currentDate.getMonth(),
          currentDate.getDate() - 2,
          17,
          0
        ),
        manager: "Jennifer Wu",
        aum: "$85.3M",
        change: "+0.7%",
      },
      priority: "high" as const,
    },
    {
      id: "f5",
      title: "Sustainable Growth Fund",
      subtitle: "Monthly NAV Calculation",
      status: "completed" as TaskStatus,
      metadata: {
        deadline: new Date(2023, currentDate.getMonth(), 6, 17, 0),
        manager: "Michael Patel",
        aum: "$150.2M",
        change: "+2.1%",
      },
      priority: "low" as const,
    },
  ];

  const kycStatusTasks = [
    {
      id: "k1",
      title: "Blackrock Institutional",
      subtitle: "Corporate Entity",
      status: "approved" as TaskStatus,
      metadata: {
        investor: "Blackrock Institutional",
        fund: "Global Equity Fund",
        reviewed: new Date(2023, currentDate.getMonth() - 1, 15),
        submission: "Complete",
      },
    },
    {
      id: "k2",
      title: "JP Morgan Asset Management",
      subtitle: "Corporate Entity",
      status: "in-progress" as TaskStatus,
      metadata: {
        investor: "JP Morgan Asset Management",
        fund: "Fixed Income Opportunities",
        submission: "Missing Documents",
        reviewed: new Date(0), // Using epoch date as placeholder for type consistency
      },
    },
    {
      id: "k3",
      title: "Vanguard Group",
      subtitle: "Corporate Entity",
      status: "approved" as TaskStatus,
      metadata: {
        investor: "Vanguard Group",
        fund: "Multiple Funds",
        reviewed: new Date(2023, currentDate.getMonth(), 2),
        submission: "Complete",
      },
    },
    {
      id: "k4",
      title: "Fidelity Investments",
      subtitle: "Corporate Entity",
      status: "rejected" as TaskStatus,
      metadata: {
        investor: "Fidelity Investments",
        fund: "Emerging Markets Fund",
        reviewed: new Date(
          2023,
          currentDate.getMonth(),
          currentDate.getDate() - 3
        ),
        submission: "Failed AML Check",
      },
    },
    {
      id: "k5",
      title: "State Street Global Advisors",
      subtitle: "Corporate Entity",
      status: "pending" as TaskStatus,
      metadata: {
        investor: "State Street Global Advisors",
        fund: "Alternative Strategies Fund",
        submission: "Awaiting Review",
        reviewed: new Date(0), // Using epoch date as placeholder for type consistency
      },
    },
  ];

  const fundNavMetadataLabels = {
    deadline: "Deadline",
    manager: "Fund Manager",
    aum: "AUM",
    change: "Change",
  };

  const kycMetadataLabels = {
    investor: "Investor",
    fund: "Fund",
    reviewed: "Last Reviewed",
    submission: "Submission Status",
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div>
              <CardTitle className="text-2xl">
                WealthBee Enterpise Theme Showcase
              </CardTitle>
              <CardDescription>
                {customThemeEnabled ? "WealthBee" : "Default"} theme in{" "}
                {theme === "dark" ? "Dark" : "Light"} mode
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                variant="outline"
                size="icon"
                className="h-9 w-9"
              >
                {theme === "dark" ? (
                  <SunMoon className="h-4 w-4" />
                ) : (
                  <MoonStar className="h-4 w-4" />
                )}
              </Button>
              <Button
                onClick={handleForceUpdate}
                variant="ghost"
                size="icon"
                className="h-9 w-9"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>{/* <SimpleThemeToggle /> */}</CardContent>
      </Card>

      <Tabs defaultValue="components">
        <div className="border-b mb-4">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="components">UI Components</TabsTrigger>
            <TabsTrigger value="typography">Typography</TabsTrigger>
            <TabsTrigger value="colors">Color Palette</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>
        </div>

        {/* Components Tab */}
        <TabsContent value="components" className="space-y-6">
          {/* Form Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="font-primary">Form Controls</CardTitle>
              <CardDescription>Input fields and form elements</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    placeholder="Enter your email"
                    type="email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea id="message" placeholder="Type your message here" />
                </div>

                <div className="space-y-2">
                  <Label>Account Type</Label>
                  <RadioGroup
                    defaultValue="personal"
                    className="flex flex-col space-y-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="personal" id="personal" />
                      <Label htmlFor="personal">Personal</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="business" id="business" />
                      <Label htmlFor="business">Business</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="enterprise" id="enterprise" />
                      <Label htmlFor="enterprise">Enterprise</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select defaultValue="usd">
                    <SelectTrigger>
                      <SelectValue placeholder="Select a currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="usd">USD ($)</SelectItem>
                      <SelectItem value="eur">EUR (€)</SelectItem>
                      <SelectItem value="gbp">GBP (£)</SelectItem>
                      <SelectItem value="jpy">JPY (¥)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date</Label>
                  <Calendar
                    events={[
                      {
                        id: 1,
                        title: "Selected Date",
                        date: selectedDate || new Date(),
                        type: "approval",
                      },
                    ]}
                    className="border rounded-md p-2"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="notifications">Enable Notifications</Label>
                  <Switch id="notifications" />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="terms" />
                  <Label htmlFor="terms">Accept terms and conditions</Label>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline">Cancel</Button>
              <Button>Submit</Button>
            </CardFooter>
          </Card>

          {/* Buttons & Interactive Elements */}
          <Card>
            <CardHeader>
              <CardTitle className="font-primary">
                Buttons & Interactive Elements
              </CardTitle>
              <CardDescription>
                Button variants and interactive components
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-4">
                  <p className="text-sm font-medium">Button Variants</p>
                  <div className="space-y-2">
                    <Button className="w-full">Primary</Button>
                    <Button variant="secondary" className="w-full">
                      Secondary
                    </Button>
                    <Button variant="outline" className="w-full">
                      Outline
                    </Button>
                    <Button variant="ghost" className="w-full">
                      Ghost
                    </Button>
                    <Button variant="destructive" className="w-full">
                      Destructive
                    </Button>
                    <Button variant="link" className="w-full">
                      Link
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="text-sm font-medium">Button Sizes</p>
                  <div className="flex flex-col items-start space-y-2">
                    <Button size="lg">Large Button</Button>
                    <Button>Default Button</Button>
                    <Button size="sm">Small Button</Button>
                    <Button size="icon" className="h-9 w-9">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  <p className="text-sm font-medium mt-4">Dropdown Menu</p>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">Open Menu</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuLabel>My Account</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem>Profile</DropdownMenuItem>
                      <DropdownMenuItem>Billing</DropdownMenuItem>
                      <DropdownMenuItem>Team</DropdownMenuItem>
                      <DropdownMenuItem>Subscription</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="space-y-4">
                  <p className="text-sm font-medium">Tooltips</p>
                  <div className="flex space-x-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="icon">
                            <Settings className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Settings</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="icon">
                            <User className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>User Profile</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="outline" size="icon">
                            <CalendarIcon className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Calendar</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Display */}
          <Card>
            <CardHeader>
              <CardTitle className="font-primary">
                Data & Content Display
              </CardTitle>
              <CardDescription>
                Components for displaying content and data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-medium mb-2">Badges</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge>Default</Badge>
                      <Badge variant="secondary">Secondary</Badge>
                      <Badge variant="outline">Outline</Badge>
                      <Badge variant="destructive">Destructive</Badge>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-2">Avatars</p>
                    <div className="flex gap-2">
                      <Avatar>
                        <AvatarImage
                          src="https://github.com/shadcn.png"
                          alt="@shadcn"
                        />
                        <AvatarFallback>CN</AvatarFallback>
                      </Avatar>
                      <Avatar>
                        <AvatarFallback>WB</AvatarFallback>
                      </Avatar>
                      <Avatar>
                        <AvatarFallback>JD</AvatarFallback>
                      </Avatar>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-2">Progress</p>
                    <Progress value={progressValue} className="w-full" />
                    <div className="flex justify-between mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setProgressValue(Math.max(0, progressValue - 10))
                        }
                      >
                        Decrease
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setProgressValue(Math.min(100, progressValue + 10))
                        }
                      >
                        Increase
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-medium mb-2">Alert</p>
                    <Alert>
                      <AlertTitle>Heads up!</AlertTitle>
                      <AlertDescription>
                        You can add components to your app using the cli.
                      </AlertDescription>
                    </Alert>
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-2">Scroll Area</p>
                    <ScrollArea className="h-[200px] w-full rounded-md border p-4">
                      <div className="space-y-4">
                        {Array.from({ length: 15 }).map((_, i) => (
                          <div key={i} className="flex items-center space-x-4">
                            <div className="size-8 rounded-full bg-primary/10" />
                            <div className="space-y-1">
                              <p className="text-sm font-medium">
                                Item {i + 1}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                This is a scrollable content area that maintains
                                its width.
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Layout Components */}
          <Card>
            <CardHeader>
              <CardTitle className="font-primary">Layout Components</CardTitle>
              <CardDescription>
                UI components for structuring content
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm font-medium mb-2">Cards</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Card Title</CardTitle>
                      <CardDescription>
                        Card description goes here
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p>Card content and information</p>
                    </CardContent>
                    <CardFooter>
                      <Button variant="outline" size="sm">
                        Action
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Features</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc pl-5 space-y-2 text-sm">
                        <li>Feature one</li>
                        <li>Feature two</li>
                        <li>Feature three</li>
                      </ul>
                    </CardContent>
                    <CardFooter>
                      <Button size="sm" className="w-full">
                        Learn More
                      </Button>
                    </CardFooter>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Statistics</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total:</span>
                        <span className="font-medium">$1,245.89</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Avg:</span>
                        <span className="font-medium">$45.20</span>
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button variant="ghost" size="sm" className="ml-auto">
                        Export
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Separator</p>
                <div className="space-y-1">
                  <div className="flex h-5 items-center text-sm">
                    <span>Item 1</span>
                  </div>
                  <Separator />
                  <div className="flex h-5 items-center text-sm">
                    <span>Item 2</span>
                  </div>
                  <Separator />
                  <div className="flex h-5 items-center text-sm">
                    <span>Item 3</span>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Tabs</p>
                <Tabs defaultValue="overview" className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="analytics">Analytics</TabsTrigger>
                    <TabsTrigger value="reports">Reports</TabsTrigger>
                  </TabsList>
                  <TabsContent
                    value="overview"
                    className="p-4 border rounded-md mt-2"
                  >
                    Overview tab content goes here
                  </TabsContent>
                  <TabsContent
                    value="analytics"
                    className="p-4 border rounded-md mt-2"
                  >
                    Analytics tab content goes here
                  </TabsContent>
                  <TabsContent
                    value="reports"
                    className="p-4 border rounded-md mt-2"
                  >
                    Reports tab content goes here
                  </TabsContent>
                </Tabs>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Typography Tab */}
        <TabsContent value="typography" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-primary">
                  Font Families & Sizes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <h6 className="text-s text-muted-foreground font-primary">
                      Font Families
                    </h6>
                    <div className="border rounded-md p-3 space-y-2">
                      <p className="font-primary text-l font-semibold">
                        Primary Font (Poppins)
                      </p>
                      <p className="font-primary text-m">
                        The quick brown fox jumps over the lazy dog.
                      </p>
                      <p className="font-secondary text-l font-semibold mt-4">
                        Secondary Font (Open Sans)
                      </p>
                      <p className="font-secondary text-m">
                        The quick brown fox jumps over the lazy dog.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h6 className="text-s text-muted-foreground font-primary">
                      Font Sizes
                    </h6>
                    <div className="border rounded-md p-3 space-y-1">
                      <p className="text-xs font-secondary">
                        Extra Small (xs): 0.625rem / 10px
                      </p>
                      <p className="text-s font-secondary">
                        Small (s): 0.75rem / 12px
                      </p>
                      <p className="text-m font-secondary">
                        Medium (m): 0.875rem / 14px
                      </p>
                      <p className="text-l font-secondary">
                        Large (l): 1rem / 16px
                      </p>
                      <p className="text-xl font-secondary">
                        Extra Large (xl): 1.25rem / 20px
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-primary">
                  Headers & Text Colors
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <h6 className="text-s text-muted-foreground font-primary">
                    Header Sizes
                  </h6>
                  <div className="border rounded-md p-3 space-y-2">
                    <h1 className="text-h1 font-primary font-semibold">
                      H1 Heading
                    </h1>
                    <h2 className="text-h2 font-primary font-semibold">
                      H2 Heading
                    </h2>
                    <h3 className="text-h3 font-primary font-semibold">
                      H3 Heading
                    </h3>
                    <h4 className="text-h4 font-primary font-semibold">
                      H4 Heading
                    </h4>
                    <h5 className="text-h5 font-primary font-semibold">
                      H5 Heading
                    </h5>
                    <h6 className="text-h6 font-primary font-semibold">
                      H6 Heading
                    </h6>
                  </div>
                </div>

                <div className="space-y-2">
                  <h6 className="text-s text-muted-foreground font-primary">
                    Text Colors
                  </h6>
                  <div className="grid grid-cols-2 gap-2 border rounded-md p-3">
                    <p className="text-foreground">Foreground Text</p>
                    <p className="text-muted">Muted Text</p>
                    <p className="text-primary">Primary Text</p>
                    <p className="text-secondary">Secondary Text</p>
                    <p className="text-accent">Accent Text</p>
                    <p className="text-destructive">Destructive Text</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Colors Tab */}
        <TabsContent value="colors" className="space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">Brand Colors</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h6 className="text-s text-muted-foreground font-primary">
                    Primary Blues
                  </h6>
                  <div className="space-y-2">
                    <div className="group h-16 rounded-md flex items-end color-primary-100 overflow-hidden">
                      <div className="bg-white/90 dark:bg-black/90 w-full text-center py-1 font-s group-hover:py-2 transition-all">
                        primary-100: #1f84ff
                      </div>
                    </div>
                    <div className="group h-16 rounded-md flex items-end color-primary-110 overflow-hidden">
                      <div className="bg-white/90 dark:bg-black/90 w-full text-center py-1 font-s group-hover:py-2 transition-all">
                        primary-110: #0877ff
                      </div>
                    </div>
                    <div className="group h-16 rounded-md flex items-end color-primary-150 overflow-hidden">
                      <div className="bg-white/90 dark:bg-black/90 w-full text-center py-1 font-s group-hover:py-2 transition-all">
                        primary-150: #004dab
                      </div>
                    </div>
                    <div className="group h-16 rounded-md flex items-end color-primary-170 overflow-hidden">
                      <div className="bg-white/90 dark:bg-black/90 w-full text-center py-1 font-s group-hover:py-2 transition-all">
                        primary-170: #00397d
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h6 className="text-s text-muted-foreground font-primary">
                    Tertiary Orange
                  </h6>
                  <div className="space-y-2">
                    <div className="group h-16 rounded-md flex items-end color-tertiary-100 overflow-hidden">
                      <div className="bg-white/90 dark:bg-black/90 w-full text-center py-1 font-s group-hover:py-2 transition-all">
                        tertiary-100: #f75c03
                      </div>
                    </div>
                    <div className="group h-16 rounded-md flex items-end color-tertiary-110 overflow-hidden">
                      <div className="bg-white/90 dark:bg-black/90 w-full text-center py-1 font-s group-hover:py-2 transition-all">
                        tertiary-110: #e05403
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h6 className="text-s text-muted-foreground font-primary">
                    Neutral Grays
                  </h6>
                  <div className="space-y-2">
                    <div className="h-10 rounded-md flex items-center justify-center color-neutral-20">
                      <span className="text-black text-s">
                        neutral-20: #f1f1f1
                      </span>
                    </div>
                    <div className="h-10 rounded-md flex items-center justify-center color-neutral-40">
                      <span className="text-black text-s">
                        neutral-40: #dbdad9
                      </span>
                    </div>
                    <div className="h-10 rounded-md flex items-center justify-center color-neutral-90">
                      <span className="text-white text-s">
                        neutral-90: #a2a09f
                      </span>
                    </div>
                    <div className="h-10 rounded-md flex items-center justify-center color-neutral-150">
                      <span className="text-white text-s">
                        neutral-150: #5d5c5a
                      </span>
                    </div>
                    <div className="h-10 rounded-md flex items-center justify-center color-neutral-190">
                      <span className="text-white text-s">
                        neutral-190: #2f2e2d
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">Semantic Colors</CardTitle>
              <CardDescription>
                Current theme: {customThemeEnabled ? "WealthBee" : "Default"} in{" "}
                {theme === "dark" ? "Dark" : "Light"} mode
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                <div className="border rounded-md overflow-hidden shadow-sm">
                  <div
                    className="h-12 flex items-center justify-center"
                    style={{
                      backgroundColor: "var(--background)",
                      color: "var(--foreground)",
                    }}
                  >
                    <span className="text-s">background</span>
                  </div>
                  <div className="p-1 bg-neutral-20 dark:bg-neutral-150 text-center text-xs">
                    {theme === "dark" ? "Dark" : "Light"} Mode
                  </div>
                </div>

                <div className="border rounded-md overflow-hidden shadow-sm">
                  <div
                    className="h-12 flex items-center justify-center"
                    style={{
                      backgroundColor: "var(--card)",
                      color: "var(--card-foreground)",
                    }}
                  >
                    <span className="text-s">card</span>
                  </div>
                  <div className="p-1 bg-neutral-20 dark:bg-neutral-150 text-center text-xs">
                    {theme === "dark" ? "Dark" : "Light"} Mode
                  </div>
                </div>

                <div className="border rounded-md overflow-hidden shadow-sm">
                  <div
                    className="h-12 flex items-center justify-center"
                    style={{
                      backgroundColor: "var(--muted)",
                      color: "var(--muted-foreground)",
                    }}
                  >
                    <span className="text-s">muted</span>
                  </div>
                  <div className="p-1 bg-neutral-20 dark:bg-neutral-150 text-center text-xs">
                    {theme === "dark" ? "Dark" : "Light"} Mode
                  </div>
                </div>

                <div className="border rounded-md overflow-hidden shadow-sm">
                  <div
                    className="h-12 flex items-center justify-center"
                    style={{
                      backgroundColor: "var(--primary)",
                      color: "var(--primary-foreground)",
                    }}
                  >
                    <span className="text-s">primary</span>
                  </div>
                  <div className="p-1 bg-neutral-20 dark:bg-neutral-150 text-center text-xs">
                    {theme === "dark" ? "Dark" : "Light"} Mode
                  </div>
                </div>

                <div className="border rounded-md overflow-hidden shadow-sm">
                  <div
                    className="h-12 flex items-center justify-center"
                    style={{
                      backgroundColor: "var(--secondary)",
                      color: "var(--secondary-foreground)",
                    }}
                  >
                    <span className="text-s">secondary</span>
                  </div>
                  <div className="p-1 bg-neutral-20 dark:bg-neutral-150 text-center text-xs">
                    {theme === "dark" ? "Dark" : "Light"} Mode
                  </div>
                </div>

                <div className="border rounded-md overflow-hidden shadow-sm">
                  <div
                    className="h-12 flex items-center justify-center"
                    style={{
                      backgroundColor: "var(--accent)",
                      color: "var(--accent-foreground)",
                    }}
                  >
                    <span className="text-s">accent</span>
                  </div>
                  <div className="p-1 bg-neutral-20 dark:bg-neutral-150 text-center text-xs">
                    {theme === "dark" ? "Dark" : "Light"} Mode
                  </div>
                </div>

                <div className="border rounded-md overflow-hidden shadow-sm">
                  <div
                    className="h-12 flex items-center justify-center"
                    style={{
                      backgroundColor: "var(--destructive)",
                      color: "var(--destructive-foreground)",
                    }}
                  >
                    <span className="text-s">destructive</span>
                  </div>
                  <div className="p-1 bg-neutral-20 dark:bg-neutral-150 text-center text-xs">
                    {theme === "dark" ? "Dark" : "Light"} Mode
                  </div>
                </div>

                <div className="border rounded-md overflow-hidden shadow-sm">
                  <div
                    className="h-12 flex items-center justify-center border-2"
                    style={{ borderColor: "var(--border)" }}
                  >
                    <span className="text-s">border</span>
                  </div>
                  <div className="p-1 bg-neutral-20 dark:bg-neutral-150 text-center text-xs">
                    {theme === "dark" ? "Dark" : "Light"} Mode
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          {/* Task Lists */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">
                Task & Status Lists
              </CardTitle>
              <CardDescription>
                Display lists of tasks, funds, or status items
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AnalyticsTaskList
                  title="Fund NAV Deadlines"
                  description="Net Asset Value calculation status"
                  tasks={fundNAVTasks}
                  metadataLabels={fundNavMetadataLabels}
                />
                <AnalyticsTaskList
                  title="KYC Status"
                  description="Know Your Customer verification status"
                  tasks={kycStatusTasks}
                  metadataLabels={kycMetadataLabels}
                />
              </div>
            </CardContent>
          </Card>

          {/* Number Panels */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">Metric Panels</CardTitle>
              <CardDescription>
                Display key performance indicators
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <AnalyticsNumberPanel
                  title="Total Revenue"
                  value={1234567.89}
                  prefix="$"
                  decimals={2}
                />
                <AnalyticsNumberPanel
                  title="Growth Rate"
                  value={24.5}
                  suffix="%"
                  decimals={1}
                />
                <AnalyticsNumberPanel
                  title="Active Users"
                  value={8543}
                  decimals={0}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                <AnalyticsNumberPanel
                  title="Average Session"
                  value={12.8}
                  suffix=" min"
                  decimals={1}
                />
                <AnalyticsNumberPanel
                  title="Conversion Rate"
                  value={3.72}
                  suffix="%"
                  decimals={2}
                />
                <AnalyticsNumberPanel
                  title="Customer Satisfaction"
                  value={4.8}
                  suffix="/5"
                  decimals={1}
                />
                <AnalyticsNumberPanel
                  title="New Signups"
                  value={267}
                  prefix="+"
                  decimals={0}
                />
              </div>
            </CardContent>
          </Card>

          {/* Calendar Analytics */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">Calendar Analytics</CardTitle>
              <CardDescription>
                Visualize events and periods on calendars
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AnalyticsCalendar
                  title="Financial Calendar"
                  description="Important financial dates for the current month"
                  month={currentMonth}
                  year={currentYear}
                  events={financialCalendarEvents}
                />
                <AnalyticsCalendar
                  title="Trading Periods"
                  description="Market status and trading information"
                  month={currentMonth}
                  year={currentYear}
                  events={tradingCalendarEvents}
                />
              </div>
            </CardContent>
          </Card>

          {/* Combined Reporting Dashboard */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">
                Fund Management Dashboard
              </CardTitle>
              <CardDescription>
                Comprehensive fund oversight and management
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <AnalyticsTaskList
                    title="Critical NAV Updates"
                    tasks={fundNAVTasks.filter(
                      (task) =>
                        task.priority === "high" || task.status === "overdue"
                    )}
                    metadataLabels={fundNavMetadataLabels}
                  />
                </div>
                <div className="space-y-4">
                  <AnalyticsNumberPanel
                    title="Funds Under Management"
                    value={25}
                    decimals={0}
                  />
                  <AnalyticsNumberPanel
                    title="Average AUM"
                    value={156.5}
                    prefix="$"
                    suffix="M"
                    decimals={1}
                  />
                  <AnalyticsNumberPanel
                    title="Overdue NAVs"
                    value={3}
                    decimals={0}
                  />
                  <AnalyticsNumberPanel
                    title="Pending KYCs"
                    value={12}
                    decimals={0}
                  />
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <AnalyticsBarChart
                  title="Fund AUM Distribution"
                  description="Assets under management by fund"
                  data={[
                    {
                      label: "Global Equity",
                      value: 245.8,
                      color: "var(--primary)",
                    },
                    {
                      label: "Fixed Income",
                      value: 180.5,
                      color: "var(--primary)",
                    },
                    {
                      label: "Emerging Markets",
                      value: 120.7,
                      color: "var(--primary)",
                    },
                    {
                      label: "Alternative",
                      value: 85.3,
                      color: "var(--primary)",
                    },
                    {
                      label: "Sustainable",
                      value: 150.2,
                      color: "var(--primary)",
                    },
                  ]}
                  height={220}
                  valueFormatter={(value) => `$${value}M`}
                />
                <AnalyticsDonutChart
                  title="KYC Status Distribution"
                  data={[
                    {
                      label: "Approved",
                      value: 42,
                      color: "var(--success, var(--green-600))",
                    },
                    {
                      label: "Pending",
                      value: 18,
                      color: "var(--warning, var(--yellow-500))",
                    },
                    {
                      label: "In Progress",
                      value: 24,
                      color: "var(--info, var(--blue-500))",
                    },
                    {
                      label: "Rejected",
                      value: 6,
                      color: "var(--destructive)",
                    },
                  ]}
                  size={220}
                  thickness={50}
                />
              </div>
            </CardContent>
          </Card>

          {/* Vertical Bar Charts */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">
                Vertical Bar Charts
              </CardTitle>
              <CardDescription>
                Visualize data comparisons with vertical bars
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AnalyticsBarChart
                  title="Monthly Sales ($)"
                  description="Revenue for the last 8 months"
                  data={monthlySalesData}
                  height={280}
                  valueFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <AnalyticsBarChart
                  title="Category Performance (%)"
                  description="YTD performance by asset class"
                  data={categoryPerformanceData}
                  height={280}
                  valueFormatter={(value) => `${value > 0 ? "+" : ""}${value}%`}
                />
              </div>
            </CardContent>
          </Card>

          {/* Horizontal Bar Charts */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">
                Horizontal Bar Charts
              </CardTitle>
              <CardDescription>
                Compare values across categories with horizontal bars
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AnalyticsBarChart
                  title="Top Investments"
                  description="Assets with highest allocation"
                  data={topInvestmentsData}
                  horizontal={true}
                  height={300}
                  valueFormatter={(value) =>
                    `$${(value / 1000000).toFixed(1)}M`
                  }
                />
                <AnalyticsBarChart
                  title="Customer Ratings"
                  description="Average score out of 5"
                  data={customerRatingsData}
                  horizontal={true}
                  height={300}
                  maxValue={5}
                />
              </div>
            </CardContent>
          </Card>

          {/* Combined Charts */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">
                Combined Visualizations
              </CardTitle>
              <CardDescription>
                Different chart types working together
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="grid grid-cols-1 gap-6">
                  <AnalyticsBarChart
                    title="Age Distribution"
                    description="Customer age breakdown"
                    data={ageDistributionData}
                    height={220}
                    valueFormatter={(value) => `${value}%`}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <AnalyticsNumberPanel
                      title="Average Age"
                      value={36.5}
                      decimals={1}
                    />
                    <AnalyticsNumberPanel
                      title="Growth in 25-34"
                      value={8.3}
                      prefix="+"
                      suffix="%"
                      decimals={1}
                    />
                  </div>
                </div>
                <div>
                  <AnalyticsDonutChart
                    title="Customer Segments"
                    data={customerSegmentData}
                    size={300}
                    thickness={70}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Donut Charts */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">
                Distribution Charts
              </CardTitle>
              <CardDescription>
                Visualize proportions and distributions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <AnalyticsDonutChart
                  title="Portfolio Allocation"
                  data={portfolioAllocationData}
                  size={220}
                  thickness={50}
                />
                <AnalyticsDonutChart
                  title="Market Share"
                  data={marketShareData}
                  size={220}
                  thickness={40}
                />
                <AnalyticsDonutChart
                  title="Customer Segments"
                  data={customerSegmentData}
                  size={220}
                  thickness={60}
                />
              </div>
            </CardContent>
          </Card>

          {/* Calendar & Metrics Integration */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">
                Advanced Dashboard Examples
              </CardTitle>
              <CardDescription>
                Combined analytics components for comprehensive insights
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <AnalyticsCalendar
                    title="Trading Calendar with Performance"
                    month={currentMonth}
                    year={currentYear}
                    events={[
                      ...tradingCalendarEvents,
                      {
                        date: new Date(currentYear, currentMonth, 3),
                        type: "success" as CalendarEventType,
                        label: "+2.4% Growth",
                      },
                      {
                        date: new Date(currentYear, currentMonth, 7),
                        type: "error" as CalendarEventType,
                        label: "-1.1% Drop",
                      },
                      {
                        date: new Date(currentYear, currentMonth, 18),
                        type: "success" as CalendarEventType,
                        label: "+3.2% Growth",
                      },
                      {
                        date: new Date(currentYear, currentMonth, 22),
                        type: "error" as CalendarEventType,
                        label: "-0.7% Drop",
                      },
                    ]}
                  />
                </div>
                <div className="space-y-4">
                  <AnalyticsNumberPanel
                    title="Trading Days"
                    value={21}
                    decimals={0}
                  />
                  <AnalyticsNumberPanel
                    title="Growth Days"
                    value={12}
                    decimals={0}
                    suffix="/21"
                  />
                  <AnalyticsNumberPanel
                    title="Net Change"
                    value={4.6}
                    prefix="+"
                    suffix="%"
                    decimals={1}
                  />
                  <AnalyticsNumberPanel
                    title="Market Events"
                    value={8}
                    decimals={0}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Component Variations */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-primary">
                Component Variations
              </CardTitle>
              <CardDescription>
                Different sizes and configurations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="grid grid-cols-2 gap-4">
                  <AnalyticsNumberPanel
                    title="Daily Active Users"
                    value={2543}
                    decimals={0}
                    className="h-full"
                  />
                  <AnalyticsNumberPanel
                    title="Average Revenue"
                    value={127.45}
                    prefix="$"
                    decimals={2}
                    className="h-full"
                  />
                  <AnalyticsNumberPanel
                    title="Retention Rate"
                    value={87.2}
                    suffix="%"
                    decimals={1}
                    className="h-full"
                  />
                  <AnalyticsNumberPanel
                    title="Support Tickets"
                    value={18}
                    decimals={0}
                    className="h-full"
                  />
                </div>
                <div className="flex justify-center items-center">
                  <AnalyticsDonutChart
                    title="Revenue Sources"
                    data={[
                      {
                        label: "Subscriptions",
                        value: 65,
                        color: "var(--primary)",
                      },
                      { label: "One-time", value: 20, color: "var(--accent)" },
                      {
                        label: "Referrals",
                        value: 15,
                        color: "var(--secondary)",
                      },
                    ]}
                    size={280}
                    thickness={70}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
